import { useEffect, useState } from "react";
import { getDbHealth, type DbHealth, type DuplicateGroup } from "./actions";

// Console-visible surface for ops/DATA_CLEANUP_TOOLING_SCOPE.md's analysis --
// LIVE WooCommerce DB table sizes and duplicate-product detection, read-only.
// Never auto-deletes anything -- surfaces duplicate groups with each copy's
// own price/stock/modified date so staff can see IDENTICAL vs DIVERGED
// copies before any cleanup decision, per that scope doc's own warning.

function DuplicateGroupRow({ group }: { group: DuplicateGroup }) {
  const [open, setOpen] = useState(false);
  const diverged = new Set(group.copies.map((c) => `${c.price}|${c.stock}|${c.stockStatus}`)).size > 1;

  return (
    <>
      <tr>
        <td className="mono">{group.sku}</td>
        <td>{group.copyCount}</td>
        <td>{diverged ? <span className="tile warn" style={{ padding: "2px 8px" }}>diverged</span> : "identical"}</td>
        <td><button onClick={() => setOpen((o) => !o)}>{open ? "Hide" : "Show"} copies</button></td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} style={{ padding: 0 }}>
            <table className="manual-products" style={{ margin: "4px 0 4px 24px", width: "calc(100% - 24px)" }}>
              <thead>
                <tr><th>Post ID</th><th>Status</th><th>Modified</th><th>Price</th><th>Stock</th><th>Stock status</th></tr>
              </thead>
              <tbody>
                {group.copies.map((c) => (
                  <tr key={c.postId}>
                    <td className="mono">{c.postId}</td>
                    <td>{c.status}</td>
                    <td>{c.modified}</td>
                    <td>{c.price ?? "—"}</td>
                    <td>{c.stock ?? "—"}</td>
                    <td>{c.stockStatus ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

export function DataHealthPanel() {
  const [data, setData] = useState<DbHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getDbHealth().then((r) => {
      setLoading(false);
      if (r.ok) setData(r.data); else setError(r.error);
    });
  };

  useEffect(load, []);

  return (
    <div>
      <h1>Data health</h1>
      <p className="meta">
        Live WooCommerce database analysis — table sizes and duplicate-product
        detection. Read-only; never deletes anything. See{" "}
        <code>ops/DATA_CLEANUP_TOOLING_SCOPE.md</code> for the full writeup.
        Requires a working SSH path to the WooCommerce server — this may fail
        with a real error if run somewhere InMotion's firewall doesn't allow yet.
      </p>

      <button onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>

      {error && <div className="panel error">Error: {error}</div>}

      {data && (
        <>
          <div className="tilegrid" style={{ marginTop: 12 }}>
            <div className="tile"><div className="tile-value">{data.totalMb.toLocaleString()} MB</div><div className="tile-label">Total DB size</div></div>
            <div className="tile"><div className="tile-value">{data.tableCount}</div><div className="tile-label">Tables</div></div>
            <div className="tile warn"><div className="tile-value">{data.duplicateSkuCount.toLocaleString()}</div><div className="tile-label">Duplicate SKU groups</div></div>
          </div>

          <h2 style={{ marginTop: 20 }}>Largest tables</h2>
          <div className="manual-table">
            <table className="manual-products">
              <thead><tr><th>Table</th><th>Size (MB)</th><th>Rows (approx.)</th></tr></thead>
              <tbody>
                {data.tables.map((t) => (
                  <tr key={t.tableName}>
                    <td className="mono">{t.tableName}</td>
                    <td>{t.sizeMb.toLocaleString()}</td>
                    <td>{t.rowsApprox.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={{ marginTop: 20 }}>
            Duplicate product groups (first 20 of {data.duplicateSkuCount.toLocaleString()} shown, with per-copy detail)
          </h2>
          <p className="meta">
            "Diverged" means the copies disagree on price/stock — a real decision
            needed on which is authoritative, not a safe auto-delete.
          </p>
          <div className="manual-table">
            <table className="manual-products">
              <thead><tr><th>SKU</th><th>Copies</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {data.duplicateGroups.map((g) => <DuplicateGroupRow key={g.sku} group={g} />)}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
